import { IRevisione, MyVerbale } from '../models/interfaces/revisione.interface';
import { IDatiBusiness } from '../models/interfaces/dati-business.interface';
import { createReducer, on } from '@ngrx/store';
import { Revisione53CoreActions } from './revisione53.actions';
import { cloneDeep, isArray, merge, mergeWith } from 'lodash-es';
import { normalizeArraysNorme } from '../forms/amm-controllo/common/helpers/norme-normalize.helper';
import { normalizeContrattiUsatiArrays } from '../forms/base-sociale/common/helpers/base-sociale-normalize.helper';

export const STATE_KEY_REVISIONE53 = 'revisione53';

const mergeCustomizer = (objValue, srcValue) => {
    if (isArray(objValue)) {
        return srcValue;
    }
};

// Store entry points: amm-controllo arrays may arrive null / {} (API, XML import, local backup).
// `revisione` must be a mutable clone.
const normalizeRevisione = (revisione: IRevisione): IRevisione => {
    normalizeArraysNorme(revisione?.dati?.my_Verbale);
    normalizeContrattiUsatiArrays(revisione?.dati?.my_Verbale);
    return revisione;
};

export interface Revisione53State {
    revisione: IRevisione;
    datiBusiness: IDatiBusiness;
    // true when a verbale update threw and was skipped (see guardedUpdate).
    updateError?: boolean;
}

// A throwing reducer terminates the NgRx state stream (every later dispatch ignored):
// skip the update, keep the store alive and flag it so the UI can warn the user.
const guardedUpdate = (state: Revisione53State, actionName: string, update: () => Revisione53State): Revisione53State => {
    try {
        return update();
    } catch (error) {
        console.error(`[${actionName}] store update failed, update skipped`, error);
        return { ...state, updateError: true };
    }
};

const withMyVerbale = (state: Revisione53State, my_Verbale: MyVerbale): Revisione53State => ({
    ...state,
    revisione: {
        ...state.revisione,
        dati: {
            ...state.revisione.dati,
            my_Verbale,
        },
    },
});

const initialState: Revisione53State = {
    revisione: null,
    datiBusiness: null,
};

export const Revisione53Reducer = createReducer(
    initialState,

    on(Revisione53CoreActions.hydrateRevisione, (state, { revisione }) => {
        return {
            ...state,
            revisione: normalizeRevisione(cloneDeep(revisione)),
        };
    }),

    on(Revisione53CoreActions.hydrateDatiBusiness, (state, { datiBusiness }) => {
        return {
            ...state,
            datiBusiness,
        };
    }),

    on(Revisione53CoreActions.revisione53hydrated, (state, { datiBusiness, revisione }) => ({
        ...state,
        datiBusiness,
        revisione: normalizeRevisione(cloneDeep(revisione)),
    })),

    on(Revisione53CoreActions.updateRevisione, (state, { revisione }) =>
        guardedUpdate(state, 'updateRevisione', () => ({
            ...state,
            revisione: normalizeRevisione(merge(cloneDeep(state.revisione), revisione, mergeCustomizer)),
        }))
    ),

    on(Revisione53CoreActions.updateMyVerbale, (state, { my_Verbale }) =>
        guardedUpdate(state, 'updateMyVerbale', () =>
            withMyVerbale(state, normalizeArraysNorme(mergeWith(cloneDeep(state.revisione?.dati.my_Verbale), my_Verbale, mergeCustomizer)))
        )
    ),

    on(Revisione53CoreActions.updateMyVerbaleFromListener, (state, { callback }) => {
        const currentVerbale = state.revisione?.dati?.my_Verbale;
        if (!currentVerbale) return state;
        return guardedUpdate(state, 'updateMyVerbaleFromListener', () =>
            withMyVerbale(state, normalizeArraysNorme(mergeWith(cloneDeep(currentVerbale), callback(currentVerbale), mergeCustomizer)))
        );
    }),

    on(Revisione53CoreActions.autosaveDataUltSalvataggio, Revisione53CoreActions.updateDataUltSalvataggio, (state) => {
        return {
            ...state,
            revisione: {
                ...state.revisione,
                dataultimosalvataggio: new Date().getTime(),
            },
        };
    }),

    on(Revisione53CoreActions.updateDataUltSalvataggioAndMd5, (state, { localSaveData }) => {
        return {
            ...state,
            revisione: {
                ...state.revisione,
                dataultimosalvataggio: localSaveData.dataultimosalvataggio,
                md5: localSaveData.md5,
            },
        };
    }),

    on(Revisione53CoreActions.clearRevisione, () => initialState)
);
